import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatsCard } from '../../components/dashboard/StatsCard';
import { RecentConversations } from '../../components/dashboard/RecentConversations';
import { QuickActions } from '../../components/dashboard/QuickActions';
import { UsageOverview } from '../../components/dashboard/UsageOverview';
import { ProjectSelector } from '../../components/projects/ProjectSelector';
import { useProjects } from '../../hooks/useProjects';
import { useAgents } from '../../hooks/useAgents';
import { useBilling } from '../../hooks/useBilling';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useDashboardInsights } from '../../hooks/useDashboardInsights';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  MessageSquare, 
  Users, 
  TrendingUp, 
  Zap,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  Target,
  Calendar,
  ChevronDown,
  Plus
} from 'lucide-react';

export function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { projects, isLoading: isLoadingProjects } = useProjects();
  const { useUsageStats } = useAgents();
  const { useUsage } = useBilling();
  
  // Project-level state management
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Initialize with first project when projects load
  useEffect(() => {
    if (projects && projects.length > 0 && !selectedProject) {
      const firstProject = projects[0];
      setSelectedProject(firstProject);
      setSelectedProjectId(firstProject.id);
    }
  }, [projects, selectedProject]);

  // Project-specific analytics
  const analytics = useAnalytics(selectedProjectId || undefined);
  const { data: analyticsData, isLoading: isLoadingAnalytics } = analytics.useOverview({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
    to: new Date()
  });

  // Check if current project has GA4 connection
  const hasGA4Connection = selectedProject?.ga4Connected || 
    (selectedProject?.project_connections && 
     selectedProject.project_connections.some((conn: any) => 
       conn.connection_type === 'ga4' && conn.status === 'connected'
     )) ||
    !!selectedProject?.ga4_property_id;

  // Project-specific usage stats
  const { data: usageStats, isLoading: isLoadingUsage } = useUsageStats('30d');
  const { data: billingUsage, isLoading: isLoadingBilling } = useUsage();
  
  // Project-specific AI insights
  const { 
    insights, 
    isLoading: isLoadingInsights, 
    error: insightsError,
    criticalInsights,
    highInsights,
    mediumInsights,
    areInsightsStale,
    getLastUpdatedText
  } = useDashboardInsights(selectedProjectId || undefined);

  // Show loading state while authentication is loading
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show project creation prompt if no projects exist
  if (!isLoadingProjects && projects.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Create Your First Project</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Get started by creating a project to connect your Google Analytics and start analyzing your data.
          </p>
          <Button 
            onClick={() => navigate('/projects/new')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </Button>
        </div>
      </div>
    );
  }

  // Handle project selection
  const handleProjectChange = (project: any) => {
    setSelectedProject(project);
    setSelectedProjectId(project.id);
  };

  // Calculate stats from real analytics data
  const calculateStats = () => {
    const overview = analyticsData?.overview;
    if (!overview) {
      return [
        {
          title: 'Total Sessions',
          value: '...',
          change: '...',
          trend: 'up' as const,
          icon: BarChart3,
          color: 'blue' as const,
        },
        {
          title: 'Active Users',
          value: '...',
          change: '...',
          trend: 'up' as const,
          icon: Users,
          color: 'green' as const,
        },
        {
          title: 'Conversations',
          value: usageStats?.stats?.totalConversations?.toString() || '0',
          change: usageStats?.stats?.conversationGrowth || '0%',
          trend: parseFloat(usageStats?.stats?.conversationGrowth || '0') >= 0 ? 'up' as const : 'down' as const,
          icon: MessageSquare,
          color: 'purple' as const,
        },
        {
          title: 'Conversion Rate',
          value: '...',
          change: '...',
          trend: 'up' as const,
          icon: TrendingUp,
          color: 'pink' as const,
        },
      ];
    }

    return [
      {
        title: 'Total Sessions',
        value: overview.sessions?.toLocaleString() || '0',
        change: '+12.5%', // TODO: Calculate from previous period
        trend: 'up' as const,
        icon: BarChart3,
        color: 'blue' as const,
      },
      {
        title: 'Active Users',
        value: overview.users?.toLocaleString() || '0',
        change: '+8.2%', // TODO: Calculate from previous period
        trend: 'up' as const,
        icon: Users,
        color: 'green' as const,
      },
      {
        title: 'Conversations',
        value: usageStats?.stats?.totalConversations?.toString() || '0',
        change: usageStats?.stats?.conversationGrowth || '0%',
        trend: parseFloat(usageStats?.stats?.conversationGrowth || '0') >= 0 ? 'up' as const : 'down' as const,
        icon: MessageSquare,
        color: 'purple' as const,
      },
      {
        title: 'Conversion Rate',
        value: overview.conversionRate ? `${overview.conversionRate.toFixed(1)}%` : '0%',
        change: '+2.1%', // TODO: Calculate from previous period
        trend: 'up' as const,
        icon: TrendingUp,
        color: 'pink' as const,
      },
    ];
  };

  const stats = calculateStats();

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Welcome back, {user?.user_metadata?.name?.split(' ')[0] || user?.email?.split('@')[0]}!
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm sm:text-base">
            {selectedProject 
              ? `Here's what's happening with ${selectedProject.name} today.`
              : "Select a project to view your marketing data."
            }
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          {/* Project Selector */}
          {projects.length > 0 && (
            <div className="relative">
              <select
                value={selectedProjectId || ''}
                onChange={(e) => {
                  const project = projects.find((p: any) => p.id === e.target.value);
                  if (project) handleProjectChange(project);
                }}
                className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 w-full sm:min-w-[200px] cursor-pointer shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {!selectedProject && <option value="" className="text-gray-500 dark:text-gray-400">Select Project</option>}
                {projects.map((project: any) => (
                  <option key={project.id} value={project.id} className="text-gray-900 dark:text-gray-100">
                    {project.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
          )}
          
          {/* AI Agent Button */}
          <Button 
            onClick={() => navigate('/agents/data')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 w-full sm:w-auto"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Ask AI Agent
          </Button>
        </div>
      </div>

      {/* Project Selection Notice */}
      {!selectedProject && projects.length > 0 && (
        <Card className="p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <div className="flex items-start sm:items-center">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 mr-3 flex-shrink-0 mt-0.5 sm:mt-0" />
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-200">Select a Project</h3>
              <p className="text-sm text-amber-700 dark:text-amber-300">Choose a project to view analytics data and insights.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {stats.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        {/* Left Column - 2/3 width on desktop, full width on mobile */}
        <div className="xl:col-span-2 space-y-4 sm:space-y-6">
          {/* AI Insights & Recommendations */}
          <Card className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-2 sm:space-y-0">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">AI Insights & Recommendations</h2>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded self-start sm:self-auto">
                {insights.length > 0 ? `${getLastUpdatedText()}${areInsightsStale() ? ' (Stale)' : ''}` : 'No Data'}
              </span>
            </div>
            
            {selectedProject ? (
              <div className="space-y-4">
                {isLoadingInsights ? (
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  </div>
                ) : insightsError ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                      {(insightsError as any)?.response?.status === 400 && !hasGA4Connection
                        ? 'Connect Google Analytics to see AI insights'
                        : 'Unable to generate AI insights. Please try again later.'
                      }
                    </p>
                    {(insightsError as any)?.response?.status === 400 && !hasGA4Connection && (
                      <Button 
                        size="sm"
                        onClick={() => navigate(`/projects/${selectedProjectId}?tab=connections`)}
                      >
                        Connect GA4
                      </Button>
                    )}
                    {hasGA4Connection && (
                      <Button 
                        size="sm"
                        onClick={() => window.location.reload()}
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                ) : insights.length === 0 ? (
                  <div className="text-center py-8">
                    <Sparkles className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      No insights available yet. Check back after more data is collected.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Show actual AI insights */}
                    {criticalInsights.map((insight: any) => (
                      <div key={insight.id} className="border-l-4 border-red-500 pl-4 py-2 bg-red-50 dark:bg-red-900/20">
                        <div className="flex items-center mb-2">
                          <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mr-2" />
                          <span className="text-sm font-semibold text-red-800 dark:text-red-300">Critical Priority</span>
                        </div>
                        <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                          {insight.description}
                        </p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                          onClick={() => navigate(`/projects/${selectedProjectId}/insights/${insight.id}`)}
                        >
                          View Details
                        </Button>
                      </div>
                    ))}

                    {highInsights.map((insight: any) => (
                      <div key={insight.id} className="border-l-4 border-orange-500 pl-4 py-2 bg-orange-50 dark:bg-orange-900/20">
                        <div className="flex items-center mb-2">
                          <TrendingUp className="w-4 h-4 text-orange-600 dark:text-orange-400 mr-2" />
                          <span className="text-sm font-semibold text-orange-800 dark:text-orange-300">High Priority</span>
                        </div>
                        <p className="text-sm text-orange-700 dark:text-orange-300 mb-2">
                          {insight.description}
                        </p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                          onClick={() => navigate(`/projects/${selectedProjectId}/insights/${insight.id}`)}
                        >
                          Get Recommendations
                        </Button>
                      </div>
                    ))}

                    {mediumInsights.slice(0, 1).map((insight: any) => (
                      <div key={insight.id} className="border-l-4 border-yellow-500 pl-4 py-2 bg-yellow-50 dark:bg-yellow-900/20">
                        <div className="flex items-center mb-2">
                          <Target className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                          <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">Medium Priority</span>
                        </div>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                          {insight.description}
                        </p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30"
                          onClick={() => navigate(`/projects/${selectedProjectId}/insights/${insight.id}`)}
                        >
                          Create Plan
                        </Button>
                      </div>
                    ))}

                    {/* Show total insights count if there are more */}
                    {insights.length > 3 && (
                      <div className="text-center pt-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/projects/${selectedProjectId}/insights`)}
                        >
                          View all {insights.length} insights
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Sparkles className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Select a project to see AI-powered insights</p>
              </div>
            )}
          </Card>

          {/* Quick Actions */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Actions</h2>
              <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <QuickActions selectedProject={selectedProject} />
          </Card>

          {/* Recent Activity */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Conversations</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/agents')}>
                View all
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <RecentConversations projectId={selectedProjectId} />
          </Card>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-6">
          {/* Usage Overview */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Usage Overview</h2>
            <UsageOverview 
              isLoading={isLoadingBilling} 
              usage={billingUsage ? {
                apiCalls: { 
                  used: billingUsage.currentCounts?.api_calls || 0, 
                  limit: billingUsage.limits?.max_api_calls || 2000 
                },
                aiTokens: { 
                  used: billingUsage.currentCounts?.ai_tokens || 0, 
                  limit: billingUsage.limits?.max_ai_requests || 20000 
                },
                projects: { 
                  used: billingUsage.currentCounts?.projects || 0, 
                  limit: billingUsage.limits?.max_projects || 3 
                },
                seats: { 
                  used: billingUsage.currentCounts?.team_members || 0, 
                  limit: billingUsage.limits?.max_team_members || 3 
                }
              } : undefined}
            />
          </Card>

          {/* Conversion Funnel Overview */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Conversion Funnel</h3>
              <Button size="sm" variant="ghost" onClick={() => navigate('/analytics/conversions')}>
                View Details
              </Button>
            </div>
            
            {selectedProject && analyticsData?.overview ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Sessions</span>
                  <span className="font-medium text-gray-900 dark:text-white">{analyticsData.overview.sessions?.toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '100%' }}></div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Engaged Users</span>
                  <span className="font-medium text-gray-900 dark:text-white">{Math.floor((analyticsData.overview.users || 0) * 0.7).toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-green-600 h-2 rounded-full" style={{ width: '70%' }}></div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Conversions</span>
                  <span className="font-medium text-gray-900 dark:text-white">{analyticsData.overview.conversions?.toLocaleString() || '0'}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${analyticsData.overview.conversionRate || 2}%` }}></div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Target className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedProject ? 'Loading funnel data...' : 'Select a project to view conversion funnel'}
                </p>
              </div>
            )}
          </Card>

          {/* Getting Started */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Getting Started</h3>
            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <div className={`w-2 h-2 ${projects.length > 0 ? 'bg-green-500' : 'bg-gray-300'} rounded-full mr-3`}></div>
                <span className={projects.length > 0 ? 'text-gray-600 dark:text-gray-300 line-through' : 'text-gray-400 dark:text-gray-500'}>
                  Create your first project
                </span>
              </div>
              <div className="flex items-center text-sm">
                <div className={`w-2 h-2 ${selectedProject?.ga4Connected ? 'bg-green-500' : 'bg-gray-300'} rounded-full mr-3`}></div>
                <span className={selectedProject?.ga4Connected ? 'text-gray-600 dark:text-gray-300 line-through' : 'text-gray-400 dark:text-gray-500'}>
                  Connect Google Analytics
                </span>
              </div>
              <div className="flex items-center text-sm">
                <div className={`w-2 h-2 ${usageStats?.stats?.totalConversations > 0 ? 'bg-green-500' : 'bg-gray-300'} rounded-full mr-3`}></div>
                <span className={usageStats?.stats?.totalConversations > 0 ? 'text-gray-600 dark:text-gray-300 line-through' : 'text-gray-400 dark:text-gray-500'}>
                  Ask your first question
                </span>
              </div>
              <div className="flex items-center text-sm">
                <div className={`w-2 h-2 bg-gray-300 rounded-full mr-3`}></div>
                <span className="text-gray-400 dark:text-gray-500">Create your first report</span>
              </div>
            </div>
            
            {projects.length > 0 && !selectedProject?.ga4Connected && (
              <Button 
                size="sm" 
                className="w-full mt-4"
                onClick={() => navigate(`/projects/${selectedProjectId}?tab=connections`)}
              >
                Connect Google Analytics
              </Button>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}